import React, { useState, useContext } from 'react'
import { Link } from 'react-router'
import { connect } from 'react-redux'
import styles from './GPTrips.scss'
import { OrgContext } from 'features/organizations/OrgContext'
import { Loadable } from 'common/Loadable'
import { DateTime } from 'common/time/DateTime'
import { Distance, Elevation, Duration, Speed } from 'common/units'
import { CircleSpinner } from 'common/spinners'
import { useFormatMessage, commonNouns, commonMetrics, privacyLevels } from 'lib/localization'
import { promiseMapAll } from 'lib/utils'
import { c } from 'lib/textUtils'
import { goalParticipantTripActions, goalParticipantTripSelectors } from 'data/resources/goalParticipantTrips'
import { defineMessages } from 'react-intl'
import _ from 'lodash'

const messages = defineMessages({
  exclude: {
    id: 'GPTrips.exclude',
    defaultMessage: 'Exclude'
  },
  private: {
    id: 'GPTrips.private',
    defaultMessage: "There are one or more private rides contributing to this user's totals"
  }
})

export const _GPTrips = ({ gpts, updateGPT, goal, trip, p, isUser, loadableResources: { gptPromise }, fm }) => {
  const [pending, setPending] = useState(false)
  const { isCurrentClub } = useContext(OrgContext)
  const hasPrivate = gptPromise?.value?.meta?.truncated_for_privacy // eslint-disable-line
  const toggleExclusion = gpt => {
    if (pending) return
    setPending(true)
    updateGPT(gpt.set('isExcluded', !gpt.isExcluded))
      .finally(() => setPending(false))
  }

  // sort by departed_at, private trips at the bottom
  const distantPast = new Date(0)
  const sorted = gpts.toList().sortBy(gpt => {
    const v = gpt.trip.departed_at
      ? new Date(gpt.trip.departed_at)
      : distantPast
    return -v
  })
  return (
    <div className={styles.tableContainer}>
      {hasPrivate && <div className={styles.private}>{fm(messages.private)}</div>}
      <table className={styles.GPTrips}>
        <thead>
          <tr className={styles.borderBottom}>
            <th>{c(fm(commonNouns.trips, { count: 1 }))}</th>
            <th>{c(fm(commonNouns.date))}</th>
            <th>{fm(commonMetrics.distance)}</th>
            <th>{fm(commonMetrics.elevation)}</th>
            <th>{c(fm(commonNouns.movingTime))}</th>
            <th>{c(fm(commonMetrics.avgSpeed).toLowerCase())}</th>
            {(isUser || isCurrentClub) && <th>{fm(messages.exclude)}</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map(gpt => <tr key={gpt.id} className={gpt.isExcluded ? styles.excluded : undefined}>
            <td>{gpt.trip.name
              ? <Link to={`/trips/${gpt.trip.id}`} className={styles.specificity}>{_.truncate(gpt.trip.name)}</Link>
              : <em>{fm(privacyLevels.private)}</em>
            }</td>
            <td>{gpt.trip.departed_at
              ? <DateTime dateOnly medium t={gpt.trip.departed_at} />
              : <em>{fm(privacyLevels.private)}</em>
            }</td>
            <td><Distance short bigUnits value={gpt.trip.distance} /></td>
            <td><Elevation short value={gpt.trip.elevation_gain} /></td>
            <td><Duration biggestUnits value={gpt.trip.moving_time} /></td>
            <td><Speed short value={gpt.trip.avg_speed} /></td>
            {(isUser || isCurrentClub) && <td>
              {pending
                ? <CircleSpinner size={16} />
                : <input type='checkbox' checked={gpt.isExcluded} onChange={() => toggleExclusion(gpt)} />}
            </td>}
          </tr>).toIndexedSeq()}
        </tbody>
      </table>
    </div>
  )
}

const LoadableGPTrips = ({ currentUser, loadGPTs, ...props }) => {
  const resourceLoaded = () => promiseMapAll({
    gptPromise: loadGPTs()
  })
  const fm = useFormatMessage()

  return <Loadable
    component={_GPTrips}
    resourceLoaded={resourceLoaded}
    currentUser={currentUser}
    fm={fm}
    {...props}
  />
}

const mapStateToProps = (state, ownProps) => {
  return {
    gpts: goalParticipantTripSelectors.byGPId(state, ownProps.p.id)
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const { goal: { id: goalId }, p: { userId } } = ownProps
  return {
    loadGPTs: () => dispatch(goalParticipantTripActions.list({ goalId, userId })),
    updateGPT: gpt => dispatch(goalParticipantTripActions.update(gpt))
  }
}

export const GPTrips = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableGPTrips)
